import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { FormFilingType } from "../types/wizard.types";
import type { BusinessFormData } from "../types/business.types";
import type { IndividualFormData } from "../types/individual.types";
import { BUSINESS_STEPS } from "../types/business.types";
import { INDIVIDUAL_STEPS } from "../types/individual.types";

type BusinessStepId = (typeof BUSINESS_STEPS)[number]["id"];
type IndividualStepId = (typeof INDIVIDUAL_STEPS)[number]["id"];

interface WizardState {
  formType: FormFilingType | null;
  currentStepIndex: number;
  completedSteps: Set<number>;
  businessData: BusinessFormData;
  individualData: IndividualFormData;
  draftId: string | null;
  /** Business form display name (when formType is BUSINESS). */
  formName: string | null;
}

interface WizardContextValue extends WizardState {
  setFormType: (type: FormFilingType) => void;
  setCurrentStepIndex: (index: number) => void;
  markStepCompleted: (index: number) => void;
  updateBusinessData: (data: Partial<BusinessFormData>) => void;
  updateIndividualData: (data: Partial<IndividualFormData>) => void;
  setDraftId: (id: string | null) => void;
  resetWizard: () => void;
  steps: readonly { id: string; title: string }[];
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  goNext: () => void;
  goPrev: () => void;
  goToStep: (index: number) => void;
}

const initialState: WizardState = {
  formType: null,
  currentStepIndex: 0,
  completedSteps: new Set(),
  businessData: {},
  individualData: {},
  draftId: null,
  formName: null,
};

const WizardContext = createContext<WizardContextValue | null>(null);

/** Forms-related data is not stored in localStorage; use API and in-memory state only. */

export function WizardProvider({
  children,
  initialFormType = null,
  initialDraftId = null,
  initialIndividualData,
  initialBusinessData,
  initialFormName = null,
}: {
  children: React.ReactNode;
  initialFormType?: FormFilingType | null;
  initialDraftId?: string | null;
  initialIndividualData?: IndividualFormData;
  initialBusinessData?: BusinessFormData;
  /** Business form display name (when formType is BUSINESS). */
  initialFormName?: string | null;
}) {
  const [state, setState] = useState<WizardState>(() => ({
    ...initialState,
    ...(initialFormType != null && { formType: initialFormType }),
    ...(initialDraftId != null && { draftId: initialDraftId }),
    ...(initialIndividualData != null && { individualData: initialIndividualData }),
    ...(initialBusinessData != null && { businessData: initialBusinessData }),
    ...(initialFormName != null && initialFormName !== "" && { formName: initialFormName }),
  }));

  const steps = state.formType === "BUSINESS" ? BUSINESS_STEPS : INDIVIDUAL_STEPS;
  const totalSteps = steps.length;
  const currentStepIndex = Math.min(state.currentStepIndex, Math.max(0, totalSteps - 1));
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;

  const setFormType = useCallback((formType: FormFilingType) => {
    setState((s) => ({
      ...s,
      formType,
      currentStepIndex: 0,
      completedSteps: new Set<number>(),
    }));
  }, []);

  const setCurrentStepIndex = useCallback((index: number) => {
    setState((s) => ({
      ...s,
      currentStepIndex: Math.max(0, Math.min(index, (s.formType === "BUSINESS" ? BUSINESS_STEPS : INDIVIDUAL_STEPS).length - 1)),
    }));
  }, []);

  const markStepCompleted = useCallback((index: number) => {
    setState((s) => ({
      ...s,
      completedSteps: new Set(s.completedSteps).add(index),
    }));
  }, []);

  const updateBusinessData = useCallback((data: Partial<BusinessFormData>) => {
    setState((s) => ({
      ...s,
      businessData: { ...s.businessData, ...data },
    }));
  }, []);

  const updateIndividualData = useCallback((data: Partial<IndividualFormData>) => {
    setState((s) => ({
      ...s,
      individualData: { ...s.individualData, ...data },
    }));
  }, []);

  const setDraftId = useCallback((draftId: string | null) => {
    setState((s) => ({ ...s, draftId }));
  }, []);

  const resetWizard = useCallback(() => {
    setState(initialState);
  }, []);

  const goNext = useCallback(() => {
    setState((s) => {
      const steps = s.formType === "BUSINESS" ? BUSINESS_STEPS : INDIVIDUAL_STEPS;
      const nextIndex = Math.min(s.currentStepIndex + 1, steps.length - 1);
      return {
        ...s,
        currentStepIndex: nextIndex,
        completedSteps: new Set(s.completedSteps).add(s.currentStepIndex),
      };
    });
  }, []);

  const goPrev = useCallback(() => {
    setState((s) => ({
      ...s,
      currentStepIndex: Math.max(s.currentStepIndex - 1, 0),
    }));
  }, []);

  const goToStep = useCallback((index: number) => {
    setState((s) => {
      const steps = s.formType === "BUSINESS" ? BUSINESS_STEPS : INDIVIDUAL_STEPS;
      const nextIndex = Math.max(0, Math.min(index, steps.length - 1));
      return { ...s, currentStepIndex: nextIndex };
    });
  }, []);

  const value = useMemo<WizardContextValue>(
    () => ({
      ...state,
      currentStepIndex,
      setFormType,
      setCurrentStepIndex,
      markStepCompleted,
      updateBusinessData,
      updateIndividualData,
      setDraftId,
      resetWizard,
      steps,
      totalSteps,
      isFirstStep,
      isLastStep,
      goNext,
      goPrev,
      goToStep,
    }),
    [
      state,
      currentStepIndex,
      setFormType,
      setCurrentStepIndex,
      markStepCompleted,
      updateBusinessData,
      updateIndividualData,
      setDraftId,
      resetWizard,
      steps,
      totalSteps,
      isFirstStep,
      isLastStep,
      goNext,
      goPrev,
      goToStep,
    ]
  );

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
}

export function useWizard() {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error("useWizard must be used within WizardProvider");
  return ctx;
}
